import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import type { PwaCalendarBooking } from '@/calendar/pwaCalendarUtils';
import {
  bookingRequiresDepositBeforeConfirm,
  bookingStatusLabel,
  changelogActionLabel,
  formatBookingDateShort,
  formatBookingDateTime,
  type PwaBookingDetail,
} from '@/calendar/pwaBookingDetail';
import { DepositCountdown } from '@/components/DepositCountdown';
import { DepositReceiptBlock } from '@/components/DepositReceiptBlock';
import { BookingSourceIcon } from '@/components/BookingSourceIcon';
import { bookingSourceLabel } from '@/calendar/bookingSource';
import { airbnbStayKind, bookingOccupancyLabel, isIcalMachineLabel } from '@/calendar/bookingListDisplay';

type Props = {
  summary: PwaCalendarBooking;
  onClose: () => void;
  onChanged?: () => void;
};

function canConfirmBookingStatus(status: string): boolean {
  return status === 'pending_deposit' || status === 'human_supervision';
}

function canConfirmBookingPermission(can: (required: string | string[]) => boolean): boolean {
  return can(['pwa.calendar.write', 'pwa.bookings.confirm']);
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="cal-detail-row">
      <span className="cal-detail-row__label">{label}</span>
      <div className="cal-detail-row__value">{children}</div>
    </div>
  );
}

export function BookingDetailSheet({ summary, onClose, onChanged }: Props) {
  const { get, post, deleteJson, can } = useSession();
  const [booking, setBooking] = useState<PwaBookingDetail | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    let cancelled = false;
    const boot = window.setTimeout(() => {
      void (async () => {
        setLoading(true);
        setLoadErr(null);
        setBooking(null);
        try {
          const data = await get<PwaBookingDetail>(
            `/api/agents/${summary.agentId}/calendar/bookings/${summary.id}`
          );
          if (!cancelled) {
            const changelog = Array.isArray(data.changelog) ? data.changelog : [];
            setBooking({ ...data, changelog });
          }
        } catch (e) {
          if (!cancelled) setLoadErr(e instanceof ApiError ? e.message : 'No se pudo cargar el detalle');
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(boot);
    };
  }, [get, summary.agentId, summary.id]);

  async function confirmBooking() {
    if (!booking) return;
    if (
      !window.confirm(
        '¿Confirmar esta reserva? Solo hacelo si revisaste la seña o el caso cuando corresponde.'
      )
    ) {
      return;
    }
    setActionErr(null);
    setConfirming(true);
    try {
      await post(`/api/agents/${summary.agentId}/calendar/bookings/${summary.id}/confirm`, {});
      onChanged?.();
      onClose();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : 'No se pudo confirmar');
    } finally {
      setConfirming(false);
    }
  }

  async function cancelBooking() {
    if (!booking || summary.bookingOrigin === 'ota') return;
    setActionErr(null);
    setCancelling(true);
    try {
      await deleteJson(`/api/agents/${summary.agentId}/calendar/bookings/${summary.id}`, {
        reason: cancelReason.trim() || undefined,
      });
      setCancelDialogOpen(false);
      setCancelReason('');
      onChanged?.();
      onClose();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : 'No se pudo cancelar');
    } finally {
      setCancelling(false);
    }
  }

  const b = booking;
  const receiptRaw = b?.depositReceiptUrl?.trim() ?? '';
  const missingRequiredReceipt = Boolean(
    b && bookingRequiresDepositBeforeConfirm(b) && !receiptRaw && canConfirmBookingStatus(b.status)
  );

  return (
    <>
      <div
        className="cal-sheet-backdrop cal-sheet-backdrop--detail"
        role="presentation"
        onClick={onClose}
      >
      <div
        className="cal-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cal-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cal-sheet__head">
          <button type="button" className="pwa-back-btn" aria-label="Volver al día" onClick={onClose}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          <h2 id="cal-detail-title" className="cal-sheet__title">
            Detalle de reserva
          </h2>
          <button type="button" className="cal-sheet__close" aria-label="Cerrar" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="cal-sheet__body">
          {loading ?
            <p className="pwa-muted">Cargando…</p>
          : loadErr ?
            <p className="pwa-err">{loadErr}</p>
          : b ?
            <>
              {actionErr ? <p className="pwa-err">{actionErr}</p> : null}
              <DetailRow label="Cliente">
                {airbnbStayKind(summary) === 'reserved' ? (
                  <strong>{isIcalMachineLabel(summary) ? bookingOccupancyLabel(summary) : b.customerName}</strong>
                ) : isIcalMachineLabel(summary) ? (
                  bookingOccupancyLabel(summary)
                ) : (
                  b.customerName
                )}
              </DetailRow>
              <DetailRow label="Estado">
                <span className={`cal-status cal-status--${b.status}`}>{bookingStatusLabel(b.status)}</span>
              </DetailRow>
              {summary.alsoBlockedOnAirbnb ?
                <DetailRow label="Airbnb">También bloqueado en Airbnb</DetailRow>
              : null}
              {b.depositExpiresAt ?
                <DetailRow label="Seña">
                  <DepositCountdown expiresAt={b.depositExpiresAt} />
                </DetailRow>
              : null}
              {b.publicReference ?
                <DetailRow label="Código">
                  <span className="cal-detail-ref">{b.publicReference}</span>
                </DetailRow>
              : null}
              <DetailRow label="Recurso">{b.resource?.name ?? summary.resourceName}</DetailRow>
              <DetailRow label="Canal">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <BookingSourceIcon
                    bookingOrigin={summary.bookingOrigin}
                    otaChannel={summary.otaChannel}
                    conversationChannel={summary.conversationChannel}
                    size={17}
                  />
                  {bookingSourceLabel(summary)}
                </span>
              </DetailRow>
              <DetailRow label="Agente">{summary.agentName}</DetailRow>
              <DetailRow label="Tipo de recurso">{b.resource?.resourceType?.name ?? '—'}</DetailRow>
              {b.resource?.resourceType?.category === 'hoteleria' && b.resource.resourceType.checkInTime ?
                <DetailRow label="Check-in">{b.resource.resourceType.checkInTime}</DetailRow>
              : null}
              {b.resource?.resourceType?.category === 'hoteleria' && b.resource.resourceType.checkOutTime ?
                <DetailRow label="Check-out">{b.resource.resourceType.checkOutTime}</DetailRow>
              : null}
              <DetailRow label="Inicio">{formatBookingDateTime(b.startDate)}</DetailRow>
              <DetailRow label="Fin">{formatBookingDateTime(b.endDate)}</DetailRow>
              <DetailRow label="Creado por">{b.createdBy === 'operator' ? 'Operador' : 'Visitante'}</DetailRow>
              {summary.conversationId ? (
                <DetailRow label="Mensaje del chat">
                  {summary.sourceMessagePreview ? <div>“{summary.sourceMessagePreview}”</div> : null}
                  <Link
                    to={`/conversations/${summary.conversationId}${
                      (b.sourceMessageId ?? summary.sourceMessageId)
                        ? `?mensaje=${encodeURIComponent(b.sourceMessageId ?? summary.sourceMessageId ?? '')}`
                        : ''
                    }`}
                  >
                    Abrir el mensaje que originó la reserva
                  </Link>
                </DetailRow>
              ) : null}
              {b.customerEmail ?
                <DetailRow label="Email">
                  <a href={`mailto:${b.customerEmail}`}>{b.customerEmail}</a>
                </DetailRow>
              : null}
              {b.customerPhone ?
                <DetailRow label="Teléfono">
                  <a href={`tel:${b.customerPhone}`}>{b.customerPhone}</a>
                </DetailRow>
              : null}
              {b.timeBlock ? <DetailRow label="Bloque horario">{b.timeBlock}</DetailRow> : null}
              {b.cancellationReason ?
                <DetailRow label="Motivo de cancelación">
                  <span className="pwa-err" style={{ fontSize: '0.9rem' }}>
                    {b.cancellationReason}
                  </span>
                </DetailRow>
              : null}
              {b.confirmedAt ?
                <DetailRow label="Confirmada">
                  {formatBookingDateTime(b.confirmedAt)}
                  {b.confirmedBy ? ` · ${b.confirmedBy}` : ''}
                </DetailRow>
              : null}
              {receiptRaw ?
                <DetailRow label="Comprobante de seña">
                  <DepositReceiptBlock rawUrl={receiptRaw} />
                </DetailRow>
              : null}

              <section className="cal-detail-changelog">
                <h3 className="cal-detail-changelog__title">Historial</h3>
                {!b.changelog?.length ?
                  <p className="pwa-muted">Sin cambios registrados.</p>
                : <ul className="cal-detail-changelog__list">
                    {b.changelog.map((entry, i) => (
                      <li key={`${entry.timestamp}-${i}`} className="cal-detail-changelog__item">
                        <div className="cal-detail-changelog__head">
                          <span className="cal-detail-changelog__action">
                            {changelogActionLabel(entry.action)}
                          </span>
                          <time className="cal-detail-changelog__time" dateTime={entry.timestamp}>
                            {formatBookingDateShort(entry.timestamp)}
                          </time>
                        </div>
                        {entry.reason ? <p className="pwa-muted">{entry.reason}</p> : null}
                        {entry.changes && Object.keys(entry.changes).length > 0 ?
                          <ul className="cal-detail-changelog__changes">
                            {Object.entries(entry.changes).map(([field, change]) => (
                              <li key={field}>
                                <strong>{field}</strong>: {String(change.from)} → {String(change.to)}
                              </li>
                            ))}
                          </ul>
                        : null}
                      </li>
                    ))}
                  </ul>}
              </section>

              {missingRequiredReceipt ?
                <p className="pwa-muted" style={{ marginTop: '0.75rem', fontSize: '0.9rem' }}>
                  Este recurso exige comprobante de seña antes de confirmar.
                </p>
              : null}

              {canConfirmBookingStatus(b.status) && canConfirmBookingPermission(can) && !missingRequiredReceipt ?
                <div className="cal-booking-row__actions" style={{ marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="pwa-btn pwa-btn--primary"
                    style={{ width: '100%' }}
                    disabled={confirming}
                    onClick={() => void confirmBooking()}
                  >
                    {confirming ? 'Confirmando…' : 'Confirmar reserva'}
                  </button>
                </div>
              : null}
              {summary.bookingOrigin !== 'ota' &&
              b.status !== 'cancelled' &&
              b.status !== 'auto_cancelled' &&
              (can('pwa.calendar.write') ||
                (b.createdBy === 'visitor' && can('pwa.bookings.confirm'))) ? (
                <div className="cal-booking-row__actions" style={{ marginTop: '0.75rem' }}>
                  <button
                    type="button"
                    className="pwa-btn"
                    style={{
                      width: '100%',
                      borderColor: '#dc2626',
                      color: '#b91c1c',
                      background: '#fff',
                    }}
                    disabled={cancelling || confirming}
                    onClick={() => {
                      setCancelReason('');
                      setCancelDialogOpen(true);
                    }}
                  >
                    {cancelling ? 'Cancelando…' : 'Cancelar reserva'}
                  </button>
                </div>
              ) : null}
            </>
          : null}
        </div>
        </div>
      </div>
      {cancelDialogOpen && b ? (
        <div
          className="pwa-confirm-backdrop"
          role="presentation"
          onClick={() => {
            if (!cancelling) setCancelDialogOpen(false);
          }}
        >
          <div
            className="pwa-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-booking-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="cancel-booking-title">Cancelar reserva</h2>
            <p>
              ¿Querés cancelar la reserva de{' '}
              <strong>{isIcalMachineLabel(summary) ? bookingOccupancyLabel(summary) : b.customerName}</strong>?
            </p>
            <label htmlFor="cancel-booking-reason">Motivo (opcional)</label>
            <textarea
              id="cancel-booking-reason"
              rows={3}
              maxLength={1000}
              value={cancelReason}
              disabled={cancelling}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            <div className="pwa-confirm-dialog__actions">
              <button
                type="button"
                className="pwa-btn"
                disabled={cancelling}
                onClick={() => setCancelDialogOpen(false)}
              >
                Volver
              </button>
              <button
                type="button"
                className="pwa-btn pwa-btn--danger"
                disabled={cancelling}
                onClick={() => void cancelBooking()}
              >
                {cancelling ? 'Cancelando…' : 'Confirmar cancelación'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
